import {
  Layout,
  Input,
  Button,
  Card,
  Typography,
  message as antdMessage,
  Alert,
  notification,
} from 'antd';
import { Outlet, useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import { CopyOutlined } from '@ant-design/icons';

import { useState, useEffect, useMemo, useRef } from 'react';
import { DownOutlined, FileTextOutlined, LogoutOutlined } from '@ant-design/icons';
import { logout } from '@/services/user';
import { isMobileDevice } from '@/utils/device';
import { markNotificationAsRead } from '@/services/notification';
import { useWebSocket, WSMessage } from '@/hooks/useWebSocket';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  currentInterviewNumberState,
  interviewDisabledState,
  currentWaitingReportState,
  pendingAckMessageIdState,
  wsConnectedState,
  allInterviewMessagesState,
} from '@/store/message';
import { rechargeOrderStatusState } from '@/store/pay';
import { InvalidTokenCode } from '@/utils/request';
import { sendQuestionnaireFeedback, sendQuestionnaireReceipt } from '@/services/questionnaire';
import { completeInterview } from '@/services/interview';

const { Paragraph } = Typography;

const { Content } = Layout;
const isMobile = isMobileDevice();

// 移动端允许访问的页面路径（前缀匹配）
const MOBILE_ALLOWED_PATHS = ['/', '/interview-experience'];

// 检查当前路径是否允许移动端访问
const isMobileAllowedPath = (pathname: string): boolean => {
  return MOBILE_ALLOWED_PATHS.some(
    (allowedPath) =>
      allowedPath === pathname ||
      (allowedPath !== '/' && pathname.startsWith(allowedPath)),
  );
};

// 处理已读回执的函数
const handleMarkAsRead = (notificationId: number) => {
  if (!isNaN(notificationId)) {
    return markNotificationAsRead(notificationId).catch(() => {
      // 标记已读失败，静默处理
      return -1;
    });
  }
  return Promise.reject(-1);
};

const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  const isHomePage = location.pathname === '/';
  const isInvitePage = location.pathname === '/invite';
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [orbitTransforms, setOrbitTransforms] = useState({
    orbit1: { x: -128, y: -128 },
    orbit2: { x: 96, y: 160 },
    orbit3: { x: 128, y: 160 },
  });

  const currentInterviewNumber = useRecoilValue(currentInterviewNumberState);
  const [, setCurrentWaitingReportState] = useRecoilState(currentWaitingReportState);
  const [disabled, setDisabled] = useRecoilState(interviewDisabledState);
  const [pendingAckMessageId, setPendingAckMessageId] = useRecoilState(pendingAckMessageIdState);
  const setWsConnected = useSetRecoilState(wsConnectedState);
  const setRechargeOrderStatus = useSetRecoilState(rechargeOrderStatusState);
  const setAllMessages = useSetRecoilState(allInterviewMessagesState);

  // WebSocket 连接管理
  const { connect, sendAck, isConnected } = useWebSocket({
    onMessage: (message: WSMessage) => {
      handleWebSocketMessage(message);
    },
  });

  // 处理 WebSocket 消息
  const handleWebSocketMessage = (message: WSMessage) => {
    switch (message.type) {
      case 'system_notification': {
        const notificationMessage = message;

        // 构建自定义按钮（如果有 url）
        const actions = notificationMessage.url
          ? [
              <Button
                key="view-detail"
                type="link"
                size="small"
                onClick={async () => {
                  await handleMarkAsRead(Number(notificationMessage.notificationId));
                  navigate(notificationMessage.url!);
                  notification.destroy(`notification-${notificationMessage.messageId}`);
                }}
              >
                查看详情
              </Button>,
            ]
          : undefined;

        // 根据 level 调用对应的 notification 方法
        // 渲染富文本 description
        const descriptionElement = notificationMessage.content ? (
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: notificationMessage.content }}
          />
        ) : undefined;

        const notificationConfig = {
          message: notificationMessage.title,
          description: descriptionElement,
          duration: 5,
          key: `notification-${notificationMessage.messageId}`,
          actions,
          onClose: async () => {
            await handleMarkAsRead(Number(notificationMessage.notificationId));
          },
        };

        switch (notificationMessage.level) {
          case 'info':
            notification.info(notificationConfig);
            break;
          case 'warning':
            notification.warning(notificationConfig);
            break;
          case 'error':
            notification.error(notificationConfig);
            break;
          case 'success':
            notification.success(notificationConfig);
            break;
        }
        break;
      }
      case 'interview_end': {
        const interviewEndMessage = message;
        const interviewNumber = interviewEndMessage.interviewNumber;
        if (
          interviewEndMessage.interviewNumber &&
          interviewEndMessage.interviewNumber === currentInterviewNumber
        ) {
          setAllMessages((prev) => {
            const currentMessages = prev[interviewNumber] || [];
            return {
              ...prev,
              [interviewNumber]: [
                ...currentMessages,
                {
                  role: 'system',
                  content: '面试结束',
                  timestamp: Date.now(),
                  messageId: `system_end_${Date.now()}`,
                },
              ],
            };
          });
          setDisabled(true);
          setTimeout(() => {
            if (interviewEndMessage.interviewNumber === currentInterviewNumber) {
              navigate(`/profile`);
            }
          }, interviewEndMessage.duration);
        }
        break;
      }
      case 'report_status': {
        const reportStatusMessage = message;
        if (
          reportStatusMessage.interviewNumber &&
          location.pathname === `/report/${reportStatusMessage.interviewNumber}`
        ) {
          setCurrentWaitingReportState({
            [reportStatusMessage.interviewNumber]: 'completed',
          });
        } else {
          // 构建自定义按钮（如果有 url）
          setCurrentWaitingReportState(null);
          const actions =
            reportStatusMessage.status === 'success' && reportStatusMessage.url
              ? [
                  <Button
                    key="view-detail"
                    type="link"
                    size="small"
                    onClick={async () => {
                      navigate(reportStatusMessage.url!);
                      notification.destroy(`notification-${reportStatusMessage.messageId}`);
                    }}
                  >
                    查看详情
                  </Button>,
                ]
              : undefined;
          const notificationConfig = {
            message: reportStatusMessage.title,
            description: reportStatusMessage.message,
            duration: 5,
            key: `notification-${reportStatusMessage.messageId}`,
            actions,
          };
          notification.info(notificationConfig);
        }
        break;
      }
      case 'questionnaire_push': {
        const questionnairePushMessage = message;

        const questionnaireNumber = questionnairePushMessage.questionnaireNumber;
        // 渲染富文本 description
        const descriptionElement = questionnairePushMessage.description ? (
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: questionnairePushMessage.description }}
          />
        ) : undefined;

        // 构建自定义按钮（如果有 url）
        const notificationConfig = {
          key: `notification-${questionnairePushMessage.messageId}`,
          message: questionnairePushMessage.title,
          description: descriptionElement,
          duration: 0,
          actions: [
            <Button
              key="view-detail"
              type="link"
              size="small"
              onClick={async () => {
                navigate(`/questionnaire/${questionnaireNumber}`);
                notification.destroy(`notification-${questionnairePushMessage.messageId}`);
              }}
            >
              查看详情
            </Button>,
          ],
          onClose: async () => {
            sendQuestionnaireFeedback(questionnairePushMessage.messageId, 'later');
          },
        };
        notification.info(notificationConfig);

        // 发送消息收到回执
        sendQuestionnaireReceipt(questionnairePushMessage.messageId);

        break;
      }
      case 'recharge_result': {
        const rechargeResultMessage = message;
        if (rechargeResultMessage.orderNo && rechargeResultMessage.status) {
          setRechargeOrderStatus((prev) => ({
            ...prev,
            [rechargeResultMessage.orderNo]: rechargeResultMessage.status,
          }));
        }
        if (rechargeResultMessage.status === 'PAID') {
          antdMessage.success('充值成功');
        } else {
          antdMessage.error(rechargeResultMessage.message || '充值失败');
        }
        break;
      }
      case 'error': {
        const errorMsg = message as typeof message & { code: number; message: string };
        antdMessage.error(errorMsg.message);
        if (errorMsg.code === InvalidTokenCode) {
          navigate(`/login?from=${encodeURIComponent(window.location.href)}`);
        }
        break;
      }
    }
  };

  // 应用启动时连接 WebSocket（全局连接，只连接一次）
  useEffect(() => {
    if (window.config?.token) {
      connect().catch(() => {
        // WebSocket 连接失败，由错误处理逻辑处理
      });
    }
    return () => {
      // 清理函数
    };
  }, [connect]);

  // 监听连接状态变化
  useEffect(() => {
    setWsConnected(isConnected);
  }, [isConnected, setWsConnected]);

  // 监听待发送确认消息
  useEffect(() => {
    if (pendingAckMessageId && isConnected) {
      sendAck(pendingAckMessageId);
      setPendingAckMessageId(null);
    }
  }, [pendingAckMessageId, isConnected, sendAck, setPendingAckMessageId]);

  // 监听路由切换，当从面试页面跳转到其他页面时，调用 completeInterview
  const previousPathRef = useRef(location.pathname);
  const isInitialMountRef = useRef(true);
  const hasCalledCompleteRef = useRef(false);

  useEffect(() => {
    // 跳过初始挂载
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      previousPathRef.current = location.pathname;
      return;
    }

    // 如果路径没有变化，不需要处理
    if (location.pathname === previousPathRef.current) {
      return;
    }

    const previousPath = previousPathRef.current;
    const currentPath = location.pathname;

    // 检查是否从面试页面（/interview/:interviewNumber）跳转到其他页面
    const isLeavingInterviewPage =
      previousPath.startsWith('/interview/') && !currentPath.startsWith('/interview/');

    if (
      isLeavingInterviewPage &&
      currentInterviewNumber &&
      !disabled &&
      !hasCalledCompleteRef.current
    ) {
      // 从面试页面跳转到其他页面，且面试未结束
      hasCalledCompleteRef.current = true;
      const interviewNum = currentInterviewNumber;

      console.log('关闭面试2');
      // 调用 completeInterview 接口
      completeInterview(interviewNum).catch(() => {
        // 忽略错误
      });
    }

    // 如果进入面试页面，重置标记
    if (currentPath.startsWith('/interview/')) {
      hasCalledCompleteRef.current = false;
    }

    // 更新上一个路径
    previousPathRef.current = location.pathname;
  }, [location.pathname, navigationType, currentInterviewNumber, disabled]);

  const showGuide = useMemo(() => {
    // 移动端且不在邀请页，且不在允许访问的路径列表中
    return isMobile && !isInvitePage && !isMobileAllowedPath(location.pathname);
  }, [isMobile, isInvitePage, location.pathname]);

  const handleToHome = () => {
    navigate('/');
  };

  /**
   * 根据鼠标位置、圆心和半径计算轨道点坐标，并支持正/负值决定弧度正向或反向。
   * @param mouseX 鼠标x坐标（屏幕绝对位置）
   * @param center 圆心 [x, y]
   * @param path 半径，>0
   * @param direction 正负方向，1为正向，-1为逆向，默认1
   * @param speed 速度，默认 1
   */
  const calculateOrbitPosition = (
    mouseX: number,
    center: [number, number],
    path: number,
    direction: 1 | -1 = 1,
    speed: number = 1,
  ) => {
    // 1. 获取屏幕可见的最大宽度
    const screenWidth = window.innerWidth;

    // 2. 根据鼠标当前在 x 相对屏幕宽度的百分比，速度变量影响百分比的计算
    const percentage = (mouseX * speed) / screenWidth;

    // 3. 将百分比换算成弧度值（0-1 映射到 0-2π），通过 direction 决定旋转方向
    const angle = percentage * 2 * Math.PI * direction;

    // 4. 以 center 作为原点，path 作为半径，计算当前弧度下的相对位置
    const x = center[0] + path * Math.cos(angle);
    const y = center[1] + path * Math.sin(angle);

    // 5. 返回结果
    return { x, y };
  };

  useEffect(() => {
    // 使用 requestAnimationFrame 节流鼠标移动事件
    let rafId: number | null = null;
    let lastMouseX = 0;

    const handleMouseMove = (e: MouseEvent) => {
      lastMouseX = e.clientX;

      // 如果已经有待处理的帧，不再添加新的
      if (rafId !== null) return;

      rafId = requestAnimationFrame(() => {
        rafId = null;

        // 定义三个光圈的圆心和半径
        const orbit1Center: [number, number] = [-28, -28];
        const orbit1Path = 100;

        const orbit2Center: [number, number] = [-4, 60];
        const orbit2Path = 120;

        const orbit3Center: [number, number] = [52, 0];
        const orbit3Path = 180;

        setOrbitTransforms({
          orbit1: calculateOrbitPosition(lastMouseX, orbit1Center, orbit1Path, 1, 0.5),
          orbit2: calculateOrbitPosition(lastMouseX, orbit2Center, orbit2Path, -1, 1.5),
          orbit3: calculateOrbitPosition(lastMouseX, orbit3Center, orbit3Path, 1, 1),
        });
      });
    };

    if (!isMobile) {
      window.addEventListener('mousemove', handleMouseMove, { passive: true });
    }
    return () => {
      if (!isMobile) {
        window.removeEventListener('mousemove', handleMouseMove);
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
      }
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // 处理复制链接
  const handleCopyLink = async () => {
    const currentUrl = window.location.href;
    try {
      await navigator.clipboard.writeText(currentUrl);
      antdMessage.success('链接已复制到剪贴板');
    } catch {
      // 降级方案：使用传统方法
      const textArea = document.createElement('textarea');
      textArea.value = currentUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        antdMessage.success('链接已复制到剪贴板');
      } catch {
        antdMessage.error('复制失败，请手动复制浏览器地址栏中的链接');
      } finally {
        document.body.removeChild(textArea);
      }
    }
  };

  return (
    <Layout className="w-full bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      {/* Skip Link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-indigo-600 focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-white focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-600"
      >
        跳转到主内容
      </a>

      <nav className="inset-x-0 top-0 z-50 bg-white/95 backdrop-blur dark:bg-slate-900/95">
        <div className="mx-auto flex items-center justify-between h-16 lg:h-20 px-6 lg:px-12 max-w-[1440px]">
          {/* logo */}
          <div className="flex cursor-pointer items-center gap-3" onClick={handleToHome}>
            <img
              src="https://project-poster-assets.oss-cn-hangzhou.aliyuncs.com/logo.png"
              alt="小灵面试"
              className="h-9 w-9 rounded-lg object-cover"
            />
            <span className="text-xl font-bold text-slate-900 dark:text-white">小灵面试</span>
          </div>
          {/* 右侧操作按钮和用户信息 */}
          {isLoginPage || isInvitePage || isMobile ? null : (
            <div className="flex items-center gap-4">
              {window.config?.username ? (
                <div className="relative">
                  <button
                    className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:text-slate-300 dark:hover:bg-slate-800"
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setShowUserMenu(false);
                      }
                    }}
                    aria-expanded={showUserMenu}
                    aria-haspopup="true"
                    aria-label="用户菜单"
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-600 text-sm font-semibold text-white shadow-sm">
                      {window.config?.username?.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium">{window.config?.username}</span>
                    <DownOutlined
                      className={`text-xs text-slate-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {showUserMenu && (
                    <div
                      className="absolute right-0 w-64 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800"
                      role="menu"
                    >
                      <div className="flex items-center gap-3 bg-slate-50 px-4 py-4 dark:bg-slate-700/50">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-600 text-base font-semibold text-white shadow-sm">
                          {window.config?.username?.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                            {window.config?.username}
                          </div>
                          <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                            {window.config?.mail}
                          </div>
                        </div>
                      </div>

                      <div className="h-px bg-slate-100 dark:bg-slate-700" />

                      <ul
                        className="space-y-1 px-2 py-2 text-sm text-slate-700 dark:text-slate-300"
                        role="none"
                      >
                        <li
                          className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 transition hover:bg-slate-50 focus:bg-slate-50 focus:outline-none dark:hover:bg-slate-700 dark:focus:bg-slate-700"
                          onClick={() => {
                            navigate('/profile');
                            setShowUserMenu(false);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              navigate('/profile');
                              setShowUserMenu(false);
                            }
                          }}
                          role="menuitem"
                          tabIndex={0}
                        >
                          <FileTextOutlined className="text-lg" />
                          <span>个人信息</span>
                        </li>
                        {/* <li className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 transition hover:bg-slate-50">
                          <BarChartOutlined className="text-lg" />
                          <span>面试报告</span>
                        </li>
                        <li className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 transition hover:bg-slate-50">
                          <SettingOutlined className="text-lg" />
                          <span>个人设置</span>
                        </li>
                        <li className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 transition hover:bg-slate-50">
                          <CreditCardOutlined className="text-lg" />
                          <span>订阅管理</span>
                        </li> */}
                      </ul>

                      <div className="h-px bg-slate-100 dark:bg-slate-700" />

                      <ul className="px-2 py-2 text-sm" role="none">
                        <li
                          className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-red-500 transition hover:bg-red-50 focus:bg-red-50 focus:outline-none dark:hover:bg-red-900/20 dark:focus:bg-red-900/20"
                          onClick={() => {
                            handleLogout();
                            setShowUserMenu(false);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleLogout();
                              setShowUserMenu(false);
                            }
                          }}
                          role="menuitem"
                          tabIndex={0}
                        >
                          <LogoutOutlined className="text-lg" />
                          <span>退出登录</span>
                        </li>
                      </ul>
                    </div>
                  )}

                  {/* 点击外部关闭菜单 */}
                  {showUserMenu && (
                    <div
                      className="fixed inset-0 z-[-1]"
                      onClick={() => setShowUserMenu(false)}
                      aria-hidden="true"
                    />
                  )}
                </div>
              ) : (
                <div className="relative">
                  <button
                    className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:text-slate-300 dark:hover:bg-slate-800"
                    onClick={() =>
                      navigate(`/login?from=${encodeURIComponent(window.location.href)}`)
                    }
                  >
                    <span className="text-sm font-medium">登录</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>
      <Content
        id="main-content"
        tabIndex={-1}
        className={`
          relative
          ${
            isHomePage
              ? 'min-h-[calc(100vh-403.75px-64px)] lg:min-h-[calc(100vh-289px-80px)]'
              : 'min-h-[calc(100vh-64px)] lg:min-h-[calc(100vh-80px)]'
          }
        `}
      >
        {showGuide ? (
          <div className="flex items-center justify-center min-h-[calc(100vh-64px)] lg:min-h-[calc(100vh-80px)]">
            <Alert
              banner
              description="检测到您正在移动端访问"
              type="warning"
              className="fixed top-16 lg:top-20 left-0 right-0 p-2 z-[1]"
            />
            <Card
              size="small"
              className="shadow-xl z-[1] w-full"
              style={{
                width: '80%',
                margin: '22rem auto',
              }}
              styles={{
                body: {
                  padding: '32px 24px',
                },
              }}
            >
              <div className="text-center mb-4">
                <Paragraph className="!mb-0 text-base text-gray-700 leading-relaxed">
                  请复制链接，使用 PC 端体验全部功能
                </Paragraph>
              </div>
              <div className="flex flex-col gap-4">
                <Input disabled value={window.location.href} />
                <Button
                  type="primary"
                  size="large"
                  icon={<CopyOutlined />}
                  onClick={handleCopyLink}
                  className="h-12 text-base font-medium"
                >
                  复制链接
                </Button>
              </div>
            </Card>
          </div>
        ) : (
          <Outlet />
        )}
        <div className="pointer-events-none absolute inset-0 z-[0] overflow-hidden opacity-[0.2]">
          <div
            className="absolute h-64 w-64 rounded-full bg-sky-200/60 blur-3xl will-change-transform"
            style={{
              left: 0,
              top: 0,
              transform: `translate(${orbitTransforms.orbit1.x}px, ${orbitTransforms.orbit1.y}px)`,
            }}
          />
          <div
            className="absolute h-72 w-72 rounded-full bg-rose-200/60 blur-3xl will-change-transform"
            style={{
              right: 0,
              top: 0,
              transform: `translate(${orbitTransforms.orbit2.x}px, ${orbitTransforms.orbit2.y}px)`,
            }}
          />
          <div
            className="absolute h-[36rem] w-[36rem] rounded-full bg-indigo-200/60 blur-3xl will-change-transform"
            style={{
              left: 0,
              top: screen.availHeight * 0.75,
              transform: `translate(${orbitTransforms.orbit3.x}px, ${orbitTransforms.orbit3.y}px)`,
            }}
          />
        </div>
      </Content>
      {isHomePage ? (
        <footer className="bg-slate-900 py-10 text-slate-300 z-[1] relative">
          <div className="mx-auto max-w-[1440px] px-6">
            <div className="grid gap-10 border-b border-slate-800 pb-8 grid-cols-1 sm:grid-cols-1 lg:grid-cols-[1.5fr_3fr]">
              <div>
                <div className="mb-4 flex items-center gap-3">
                  <img
                    src="https://project-poster-assets.oss-cn-hangzhou.aliyuncs.com/logo.png"
                    alt="小灵面试"
                    className="h-9 w-9 rounded-lg object-cover"
                  />
                  <span className="text-xl font-semibold text-white">小灵面试</span>
                </div>
                <p className="text-sm leading-relaxed text-slate-400">
                  让AI助力你的面试准备，提升求职竞争力。
                </p>
              </div>

              <div className="grid gap-8 text-sm text-slate-400 grid-cols-3 ">
                <div>
                  <h4 className="mb-3 text-sm font-semibold text-white">产品</h4>
                  <ul className="space-y-2">
                    <li>
                      <button
                        type="button"
                        className="text-left transition hover:text-white focus:outline-none focus:underline"
                      >
                        功能特性
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="text-left transition hover:text-white focus:outline-none focus:underline"
                      >
                        定价方案
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="text-left transition hover:text-white focus:outline-none focus:underline"
                      >
                        使用案例
                      </button>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="mb-3 text-sm font-semibold text-white">资源</h4>
                  <ul className="space-y-2">
                    <li>
                      <button
                        type="button"
                        className="text-left transition hover:text-white focus:outline-none focus:underline"
                      >
                        帮助中心
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="text-left transition hover:text-white focus:outline-none focus:underline"
                      >
                        面试技巧
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="text-left transition hover:text-white focus:outline-none focus:underline"
                      >
                        博客
                      </button>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="mb-3 text-sm font-semibold text-white">公司</h4>
                  <ul className="space-y-2">
                    <li>
                      <button
                        type="button"
                        className="text-left transition hover:text-white focus:outline-none focus:underline"
                      >
                        关于我们
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="text-left transition hover:text-white focus:outline-none focus:underline"
                      >
                        联系我们
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="text-left transition hover:text-white focus:outline-none focus:underline"
                      >
                        加入我们
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col items-center justify-between gap-3 text-xs text-slate-500 ">
              <p className="order-1">&copy; 2025 小灵面试. All rights reserved.</p>
              <div className="order-2 flex gap-4">
                <button
                  type="button"
                  className="transition hover:text-slate-300 focus:outline-none focus:underline"
                >
                  隐私政策
                </button>
                <button
                  type="button"
                  className="transition hover:text-slate-300 focus:outline-none focus:underline"
                >
                  服务条款
                </button>
              </div>
            </div>
          </div>
        </footer>
      ) : null}
    </Layout>
  );
};

export default AppLayout;
